import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as repo from '@/db/repositories'
import { createId } from '@/utils/id'
import { sm2, isDue, today, QUALITY_GRADE, type Quality } from '@/utils/srs'
import { createProvider } from '@/ai/factory'
import { reviewQuestionSystem, reviewQuestionUser } from '@/ai/prompts'
import { useSettingsStore } from '@/stores/settings'
import type { Highlight, ReviewCard } from '@/types/domain'

/**
 * Store de tarjetas de repaso (SRS, Área E).
 *
 * Espejo del store de `stats` en cuanto a carga, pero gestiona la lógica de
 * repetición espaciada: convertir highlights en tarjetas, calificarlas
 * (SM-2) y exponer las vencidas. Las tarjetas se sincronizan vía CRDT
 * global (`useReviewsSync`), como las estanterías.
 *
 * Las mutaciones SIEMPRE reasignan `cards.value` de forma inmutable (nunca
 * mutan in-place): el puente CRDT (`connectCollection`) no usa `deep:true`,
 * así que solo detecta reasignaciones del ref.
 */
export const useReviewsStore = defineStore('reviews', () => {
  const cards = ref<ReviewCard[]>([])
  const loading = ref(false)
  const generatingQuestion = ref(false)

  /** Tarjetas vencidas respecto a hoy, ordenadas por dueDate asc. */
  const dueCards = computed<ReviewCard[]>(() => {
    const t = today()
    return cards.value
      .filter((c) => isDue(c, t))
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
  })

  /** Conteo de vencidas hoy (para badges/anillo). */
  const dueCount = computed(() => dueCards.value.length)

  /** Set de highlightIds que ya tienen tarjeta (para el toggle del panel). */
  const highlightIds = computed(() => new Set(cards.value.map((c) => c.highlightId)))

  async function loadAll() {
    loading.value = true
    try {
      cards.value = await repo.getAllReviewCards()
    } finally {
      loading.value = false
    }
  }

  /** ¿Existe ya una tarjeta para este highlight? */
  function hasCard(highlightId: string): boolean {
    return highlightIds.value.has(highlightId)
  }

  /**
   * Crea una tarjeta a partir de un highlight (conversión explícita).
   * Idempotente: si ya existe una tarjeta para el highlight, no hace nada.
   */
  async function createFromHighlight(h: Highlight): Promise<ReviewCard | null> {
    if (hasCard(h.id)) return null
    const now = new Date().toISOString()
    const card: ReviewCard = {
      id: createId(),
      bookId: h.bookId,
      highlightId: h.id,
      text: h.text,
      note: h.note,
      chapterTitle: h.chapterTitle,
      color: h.color,
      intervalDays: 0,
      repetitions: 0,
      easeFactor: 2.5,
      lapses: 0,
      dueDate: today(),
      aiQuestion: null,
      createdAt: now,
      updatedAt: now,
    }
    await repo.saveReviewCard(card)
    cards.value = [card, ...cards.value]
    return card
  }

  /** Califica una tarjeta (SM-2) y persiste el nuevo estado. */
  async function grade(cardId: string, quality: Quality): Promise<void> {
    const idx = cards.value.findIndex((c) => c.id === cardId)
    if (idx < 0) return
    const current = cards.value[idx]
    const update = sm2(current, QUALITY_GRADE[quality])
    const updated: ReviewCard = {
      ...current,
      ...update,
      updatedAt: new Date().toISOString(),
    }
    await repo.saveReviewCard(updated)
    cards.value = [
      ...cards.value.slice(0, idx),
      updated,
      ...cards.value.slice(idx + 1),
    ]
  }

  async function remove(id: string) {
    await repo.deleteReviewCard(id)
    cards.value = cards.value.filter((c) => c.id !== id)
  }

  async function removeForHighlight(highlightId: string) {
    await repo.deleteReviewCardByHighlight(highlightId)
    cards.value = cards.value.filter((c) => c.highlightId !== highlightId)
  }

  async function removeForBook(bookId: string) {
    await repo.deleteReviewCardsForBook(bookId)
    cards.value = cards.value.filter((c) => c.bookId !== bookId)
  }

  /** Reemplaza la lista entera (puente CRDT al llegar cambios remotos). */
  function replaceAll(items: ReviewCard[]) {
    cards.value = items.slice()
  }

  /**
   * Genera una pregunta de comprensión IA para una tarjeta (#19) y la guarda.
   * Reutiliza el provider de chat activo (no embeddings).
   */
  async function generateAiQuestion(cardId: string): Promise<string | null> {
    const card = cards.value.find((c) => c.id === cardId)
    if (!card || generatingQuestion.value) return null
    generatingQuestion.value = true
    try {
      const settings = useSettingsStore().settings
      const provider = createProvider(settings.ai)
      const lang = settings.ai.summaryLanguage
      const { text } = await provider.stream(
        {
          system: reviewQuestionSystem(lang),
          user: reviewQuestionUser(card.text),
          temperature: 0.7,
          maxTokens: 120,
        },
        () => {},
      )
      const question = text.trim()
      if (!question) return null
      const idx = cards.value.findIndex((c) => c.id === cardId)
      const updated: ReviewCard = {
        ...card,
        aiQuestion: question,
        updatedAt: new Date().toISOString(),
      }
      await repo.saveReviewCard(updated)
      cards.value = [
        ...cards.value.slice(0, idx),
        updated,
        ...cards.value.slice(idx + 1),
      ]
      return question
    } finally {
      generatingQuestion.value = false
    }
  }

  return {
    cards,
    loading,
    generatingQuestion,
    dueCards,
    dueCount,
    highlightIds,
    loadAll,
    hasCard,
    createFromHighlight,
    grade,
    remove,
    removeForHighlight,
    removeForBook,
    replaceAll,
    generateAiQuestion,
  }
})
